import { useAppSelector } from "@app/hooks";
import { LModal } from "@components";
import { useUserPermission } from "@helpers/userPermissionHelper";
import { useApi } from "@services/useApi";
import { OrganizationUsers } from "@store/generalTypes";
import { flatMap, map, every, includes } from "lodash";
import { useEffect, useState } from "react";

export const MissingRolesModal = ({
    organizationUsers,
    onHasMissingRole,
    onModalClose
}: LocationCardProps): JSX.Element => {
    const [missingRoleModal, setMissingRoleModal] = useState({ showModal: false, hasMissingRole: false });
    const { organization, locations, currentLocation } = useAppSelector((x) => {
        return {
            organization: x.organization,
            locations: x.orgLocations.locations,
            currentLocation: x.orgLocations.currentLocation
        };
    });
    const { isClinicalAdmin } = useUserPermission({ useCurrentLocation: true });

    const { getOrganizationUsers } = useApi();

    useEffect(() => {
        if (organizationUsers) {
            checkSelectedLocationStatus(organizationUsers);
        } else {
            void loadUsers();
        }
    }, [currentLocation]);

    const loadUsers = async (): Promise<void> => {
        const locationsIds = locations.map((l) => l.id);

        const users = await getOrganizationUsers(organization.id!, locationsIds);

        checkSelectedLocationStatus(users);
    };

    const checkSelectedLocationStatus = (users: OrganizationUsers): void => {
        const rolesForLocation = flatMap(users.items, (user) =>
            flatMap(user.locations, (location) =>
                location.id === currentLocation?.id ? map(location.acceptedRoles, "id") : []
            )
        );

        const hasRoles = every([1, 2, 3], (role) => includes(rolesForLocation, role));

        setMissingRoleModal({ hasMissingRole: !hasRoles, showModal: !hasRoles });

        onHasMissingRole?.(!hasRoles);
    };

    return (
        <>
            {missingRoleModal.showModal && isClinicalAdmin && (
                <LModal
                    title={
                        <p className="has-text-centered has-text-weight-bold is-size-3">
                            Please assign required roles!
                        </p>
                    }
                    fullHeight={false}
                    width="s"
                    hasCancelConfirmButtons
                    confirmText={"Continue"}
                    cancelText=""
                    hasCloseButton={false}
                    divider={false}
                    onConfirm={() => {
                        setMissingRoleModal({ ...missingRoleModal, showModal: false });
                        onModalClose?.();
                    }}
                    onClose={() => {
                        onModalClose?.();
                    }}
                >
                    <div className="is-flex is-flex-direction-column">
                        <p className="content has-text-centered is-size-4">
                            In order for you to be able to utilize the LASSI-D™ Assessments, first you need to assign
                            required roles by clicking the <strong>&quot;Add staff users&quot;</strong> button
                        </p>
                    </div>
                </LModal>
            )}
        </>
    );
};

interface LocationCardProps {
    organizationUsers?: OrganizationUsers;
    onHasMissingRole?: (hasMissingRoles: boolean) => void;
    onModalClose?: () => void;
}

export default MissingRolesModal;
